import { createContext, FC, ReactNode, useContext, useRef } from 'react';
import { createStore, useStore } from 'zustand';
import { dateToGQLFormat, stripSeconds } from '../../helpers/dateHelper';
import { handleMutationResult } from '../../helpers/toastHelper';
import {
    useEndMyWorkTimeRecordMutation,
    useGetMyCurrentWorkStatusQuery,
    useStartMyWorkTimeRecordMutation,
    WorkTimeDataSource
} from '../gql/graphql.generated';
import { geolocationToGqlCoords, Position } from '../location/location-helper';

export enum ClockInStatus {
  In,
  Out,
  Unknown
}

export interface ClockOptions {
  coords?: Position;
  manualDate?: any;
  deviceId?: string;
  secureCode?: string;
}

export interface ClockService {
  clockState: ClockInStatus;
  clockLocationId?: number;
  loadingState: boolean;
  errorMessage?: string;
  setClockState: (
    newState: ClockInStatus,
    locationId: number,
    source: WorkTimeDataSource,
    options?: ClockOptions
  ) => Promise<void>;
  refreshClockState: () => void;
}

type ClockStore = ReturnType<typeof makeStore>;

const makeStore = (
  getCurrentClockState: () => { initialClockState: ClockInStatus; clockLocation: number | undefined },
  setClockStateMutation: (
    newClockState: ClockInStatus,
    locationId: number,
    source: WorkTimeDataSource,
    options?: ClockOptions
  ) => Promise<any>
) =>
  createStore<ClockService>()(set => ({
    clockState: getCurrentClockState().initialClockState,
    clockLocationId: getCurrentClockState().clockLocation,
    loadingState: false,
    setClockState: async (newClockState, locationId, source, options) => {
      set({ loadingState: true, errorMessage: undefined });

      const result = await setClockStateMutation(newClockState, locationId, source, options);
      await handleMutationResult(result, {
        successMessage: 'Clock updated successfully.',
        errorMessage: 'Failed to update clock.',
        onError: error => set({ loadingState: false, errorMessage: error.message }),
        throwOnError: true,
        onSuccess: () =>
          set({
            clockState: newClockState,
            clockLocationId: newClockState === ClockInStatus.In ? locationId : undefined,
            loadingState: false
          })
      });
    },
    refreshClockState: () => {
      set({
        clockState: getCurrentClockState().initialClockState,
        clockLocationId: getCurrentClockState().clockLocation
      });
    }
  }));

const ClockContext = createContext<ClockStore | null>(null);

export const ClockProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [{ data: workStatusData }] = useGetMyCurrentWorkStatusQuery({
    requestPolicy: 'network-only'
  });

  const startMyWorkTimeRecord = useStartMyWorkTimeRecordMutation()[1];
  const endMyWorkTimeRecord = useEndMyWorkTimeRecordMutation()[1];

  const getCurrentClockState = () => {
    let initialClockState = ClockInStatus.Unknown;
    let clockLocation: number | undefined = undefined;
    if (workStatusData?.getMyOpenWorkTimeRecord) {
      const record = workStatusData?.getMyOpenWorkTimeRecord[0];
      if (record) {
        initialClockState = ClockInStatus.In;
        clockLocation = record.locationId;
      } else {
        initialClockState = ClockInStatus.Out;
      }
    }
    return { initialClockState, clockLocation };
  };

  const setClockStateMutation = (
    newClockState: ClockInStatus,
    locationId: number,
    source: WorkTimeDataSource,
    options?: ClockOptions
  ) => {
    const gqlCoords = geolocationToGqlCoords(options?.coords);
    const date = new Date();
    stripSeconds(date);

    if (newClockState === ClockInStatus.In) {
      return startMyWorkTimeRecord({
        workTimeRecordStart: {
          locationId,
          startSource: source,
          start: options?.manualDate ?? dateToGQLFormat(date),
          startGeoLocation: gqlCoords,
          deviceId: options?.deviceId,
          verificationCode: options?.secureCode
        }
      });
    } else {
      return endMyWorkTimeRecord({
        workTimeRecordEnd: {
          locationId,
          endSource: source,
          end: options?.manualDate ?? dateToGQLFormat(date),
          endGeoLocation: gqlCoords,
          deviceId: options?.deviceId,
          verificationCode: options?.secureCode
        }
      });
    }
  };

  const storeRef = useRef<ClockStore | null>(null);
  if (!storeRef.current) {
    storeRef.current = makeStore(getCurrentClockState, setClockStateMutation);
  }

  return <ClockContext.Provider value={storeRef.current}>{children}</ClockContext.Provider>;
};

export const useClockService = <T,>(selector: (state: ClockService) => T): T => {
  const store = useContext(ClockContext);
  if (!store) {
    throw new Error('useClockService must be used within a ClockProvider');
  }
  return useStore(store, selector);
};
